import { toast } from 'react-toastify';
import { useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo } from 'react';
import { useApiLoader } from 'features/loaders/hooks/useApiLoader';
import { useDialog } from 'hooks/useDialog';
import { Sizes } from 'enums/sizes';
import { useTranslation } from 'react-i18next';
import { NsKey } from 'enums/NsKey';
import { BankAccountDto } from 'api/bank-account';
import { bankAccountsAPI } from 'services/swagger-api';
import { BankAccount } from 'components/domain/Bank/BankAccount';
import {
  useQuoteContractualizationData,
  useQuoteMandateData,
  useQuoteMandateActions,
  usePayerBankAccounts
} from '../../store';
import {
  getPayerOption,
  QuoteMandateFormFieldNames,
  QuoteMandateFormValues
} from '../../forms/quote-mandate';

export const BankAccountSelector: React.FC = () => {
  const quoteData = useQuoteContractualizationData();
  const quoteMandate = useQuoteMandateData();
  const { fetchQuoteMandate, fetchPayerBankAccounts } = useQuoteMandateActions();
  const { callWithLoading } = useApiLoader();
  const { openDialog, closeDialog, setDialogActions } = useDialog();
  const { t } = useTranslation(NsKey.Mandate);

  const payerBankAccounts = usePayerBankAccounts();
  const { values, setFieldValue } = useFormikContext<QuoteMandateFormValues>();
  const payerOption = useMemo(
    () => getPayerOption(quoteMandate, values.payer),
    [quoteMandate, values.payer]
  );

  // La liste suit le payeur choisi : comptes du souscripteur, du bénéficiaire ou du cabinet
  // du courtier connecté (le back vérifie l'appartenance). Tant que ceux du payeur choisi ne
  // sont pas chargés, la liste reste vide plutôt que d'afficher ceux d'un autre payeur.
  const bankAccounts = useMemo(() => {
    if (!quoteMandate) return [];
    if (!payerOption) return quoteMandate.bankAccounts;
    return payerBankAccounts?.payer === values.payer ? payerBankAccounts.bankAccounts : [];
  }, [quoteMandate, payerOption, payerBankAccounts, values.payer]);

  useEffect(() => {
    if (bankAccounts.length === 1 && !values.bankAccount) {
      setFieldValue(QuoteMandateFormFieldNames.BankAccount, bankAccounts[0].id);
    }
  }, [bankAccounts, values, setFieldValue]);

  const selectedMandat = useMemo(() => {
    return values.bankAccount;
  }, [values]);

  const onSelection = useCallback(
    (bankAccount: BankAccountDto) => {
      setFieldValue(QuoteMandateFormFieldNames.BankAccount, bankAccount.id);
    },
    [setFieldValue]
  );

  const handleDelete = useCallback(
    async (bankAccountId: string) => {
      openDialog(
        {
          title: 'MESSAGES.CONFIRM_DELETE_TITLE',
          size: Sizes['2xl']
        },
        <p>{t('MESSAGES.DELETE_MANDATE')}</p>
      );
      setDialogActions({
        closeLabel: 'COMMON.CANCEL',
        submitLabel: 'COMMON.VALIDATE',
        submitDisabled: false,
        onSubmit: async () => {
          if (quoteData) {
            const DELETE_BANK_ACCOUNT = 'delete-bank-account';
            const result = await callWithLoading(
              DELETE_BANK_ACCOUNT,
              () => bankAccountsAPI.deleteBankAccount({ id: bankAccountId }),
              {
                captureError: true,
                isGlobalError: true,
                errorTransform: (err: any) => {
                  return err instanceof Error
                    ? err
                    : new Error(
                        'Une erreur est survenue lors de la soumission du formulaire'
                      );
                }
              }
            );
            if (result)
              if (result.isSuccess && quoteData) {
                if (payerOption) fetchPayerBankAccounts(payerOption);
                else fetchQuoteMandate(quoteData.id);
              } else {
                toast.error(result.errorMessage);
              }
            closeDialog();
          }
        }
      });
    },
    [callWithLoading, closeDialog, fetchQuoteMandate, fetchPayerBankAccounts, payerOption, openDialog, quoteData, setDialogActions, t]
  );

  return (
    <div className="flex flex-col gap-2">
      {bankAccounts &&
        bankAccounts.map(bankAccount => (
          <BankAccount
            key={bankAccount.id}
            bankAccount={bankAccount}
            isSelected={bankAccount.id === selectedMandat}
            onSelect={onSelection}
            onDelete={handleDelete}
          />
        ))}
    </div>
  );
};
